import {
  DEFAULT_CHANNEL_SORT,
  DEFAULT_CHANNEL_TAB,
  formatRoute,
  jumpOutLinks,
  loginRoute,
  type ChannelTab,
  type ChannelVideoSort,
  type EmbedList,
  type EmbedOptions,
  type FeedKind,
  type JumpOut,
  type Route,
  type SearchQuery,
  type SettingsTab,
} from '@visious/core';
import { useRoute } from './router';
import type { GatedFeed } from '../screens/gated/model';
import { text } from '../strings';

/**
 * Route → screen. A separate step from `parseRoute` because they answer different questions:
 * `parseRoute` says what a URL *means* (and its answer is the same on Android), while this says
 * which of *this build's* screens is ready to render it. A route the URL scheme defines but no
 * slice has built yet is a normal state for most of phase 1, not an error.
 *
 * It lives in a `.ts` so `App.tsx` stays a switch over `screen.kind` with no core import (R13).
 */

export type Screen =
  /**
   * `/` while the landing redirect is in flight (`useLandingRedirect`). It renders nothing: the
   * route's only meaning is "the place opening Visious takes you", and the redirect is immediate.
   */
  | { readonly kind: 'home' }
  /**
   * §8's Popular page. It was two pages over one component until Trending was retired: those rows
   * are a *section* on this one now, above the popular ones and behind a setting, and `/feed/trending`
   * parses as this page (`FEED_KINDS`) so the old URL still lands somewhere.
   */
  | { readonly kind: 'feed' }
  /**
   * §8's Search page. It carries the whole `SearchQuery` because every filter is in the URL
   * — the page needs no state of its own, and the back button is what "undo a filter" is.
   */
  | { readonly kind: 'search'; readonly search: SearchQuery }
  /** `/hashtag/TAG` — a video list rather than a search, with its own endpoint (`{results:[…]}`). */
  | { readonly kind: 'hashtag'; readonly tag: string; readonly page: number }
  /**
   * §8's Channel page. It carries the tab and the sort for 2c's reason: both are in the URL,
   * so the page holds no state and the back button is what "go back to Videos" means. `search` is the
   * in-channel search — the same page wearing a question instead of a tab, so the header and
   * the tab strip survive while the body shows `/channels/{ucid}/search` results.
   */
  | {
      readonly kind: 'channel';
      readonly channelId: string;
      readonly tab: ChannelTab;
      readonly sort: ChannelVideoSort;
      readonly search: { readonly q: string; readonly page: number } | null;
    }
  /**
   * §8's Playlist page. It carries nothing but the id, because the endpoint takes no sort, no region and
   * no filter: there is no second thing for the URL to hold — and since 3c not even the editing state,
   * which is a panel rather than a place.
   *
   * One id, two endpoints: an `IVPL…` id read by a signed-in reader goes through
   * `/api/v1/auth/playlists/{id}`, which is the only way to see a private one. The *route* does not know
   * that and must not — the decision is `authPlaylistReadPreferred`, and it depends on the session
   * rather than on the URL.
   */
  | { readonly kind: 'playlist'; readonly playlistId: string }
  /**
   * Every parameter the watch route parsed, not just the two the player consumes: §8's jump-out
   * links are built from the route so that `t`, `list` and `index` survive the trip out of the app.
   */
  | {
      readonly kind: 'watch';
      readonly videoId: string;
      readonly startSec: number | null;
      readonly playlistId: string | null;
      readonly index: number | null;
    }
  /**
   * `/embed/ID` — the player alone (2026-08-02). The one screen `App.tsx` renders **without
   * the shell**: the page lives inside somebody else's <iframe>, and a header, a nav and a search
   * box inside a 560×315 box are furniture in a window.
   */
  | {
      readonly kind: 'embed';
      /**
       * `null` where the address names a **list and no video** (`/embed/?list=PL…`, and YouTube's
       * older `/embed/videoseries?list=PL…`). That one has to read the playlist before it knows what
       * it is showing, which is a screen of its own — `App.tsx` branches on this field.
       */
      readonly videoId: string | null;
      readonly startSec: number | null;
      /** What the embed URL asked the player to do — `autoplay`, `mute`, `controls` and the rest. */
      readonly options: EmbedOptions;
      /** The playlist this embed walks, and where in it. `null` for a single video. */
      readonly list: EmbedList | null;
    }
  /**
   * §8's two URL-scheme rows that cost a request: a channel named rather than identified,
   * and a clip. One screen, because it is one state machine — ask, then navigate or explain. It takes
   * the whole `Route` rather than the fields, since the failure page's jump-out is built from it.
   */
  | { readonly kind: 'resolve'; readonly route: Route; readonly href: string }
  /**
   * §8's sign-in page. It carries `next` for the same reason the search screen carries its
   * whole query: the destination is in the URL, so it survives the top-level navigation to the
   * instance's consent screen and back without this page holding state across it.
   */
  | { readonly kind: 'login'; readonly next: string | null }
  /**
   * §8's three account-gated destinations (§13.2). One arm for all three because the *gate* is the
   * same and only the sentences differ.
   *
   * It used to carry a `pending` placeholder for whichever feed no slice had built yet. **That was
   * removed** once the last of the three was built — see below for why that deletion was
   * the point rather than a tidy-up.
   */
  | {
      readonly kind: 'gated';
      readonly feed: GatedFeed;
      /** `/login?next=<this page>`, so approving the grant returns here. */
      readonly signInHref: string;
    }
  /**
   * §8's Settings page. It carries the topic tab and it is **not gated**: instance
   * configuration, the capability probe, the theme and the playback defaults all work signed out, and
   * the account half of the page explains itself rather than hiding. A gate here would put the one
   * page that can *change the instance* behind a credential that belongs to an instance.
   */
  | { readonly kind: 'settings'; readonly tab: SettingsTab }
  | ({ readonly kind: 'placeholder' } & PlaceholderContent);

export interface PlaceholderContent {
  readonly title: string;
  readonly detail: string;
  /** `null` for a genuine 404 — nothing is planned, because the URL means nothing. */
  readonly slice: string | null;
  readonly homeHref: string;
  /**
   * §8 asks for `/post/ID` to carry a jump-out rather than being a dead end. `jumpOutLinks(null,
   * route)` is the YouTube half only — which is the documented `config: null` behaviour and the
   * right one here: this registry is pure, and the instance origin arrives asynchronously.
   */
  readonly jumpOuts: readonly JumpOut[];
}

const HOME_HREF = formatRoute({ kind: 'home' });

/**
 * §8's four feed pages. They exist as routes because the shell draws a nav item for each
 * one, and a destination the app itself links to must not answer "Nothing here".
 *
 * Since 3a the three account-gated ones are a **gate** rather than a note: signed out they say what is
 * missing and link to a real sign-in carrying a `?next=` back to themselves (§8's signed-out
 * behaviour), and signed in they fall through to the page behind it.
 *
 * **Since 3d all three of those pages are real, and the machinery that tracked which were not is gone.**
 * It was a `PendingFeed = Exclude<GatedFeed, …>` with a `PlaceholderContent` per member, arranged so that
 * building a feed's page meant *deleting* its placeholder rather than leaving one behind that nobody
 * renders and nobody notices has gone out of date. 3b removed Subscriptions's, 3c Playlists's, and 3d
 * History's — which left the exclusion with no members, so the type, the record and the `pending` field
 * on the screen all went with it. That is the intended end state of that construction, not an accident
 * of it: it existed to make the compiler insist on its own removal.
 */

/** The gate for one feed, with the way back to it already built (`loginRoute`, so `next` is validated). */
function gatedScreen(feed: GatedFeed): Screen {
  return { kind: 'gated', feed, signInHref: formatRoute(loginRoute({ kind: 'feed', feed })) };
}

const FEED_SCREENS: Readonly<Record<FeedKind, Screen>> = {
  // The others are gated: three feeds (3b–3d) and the manage-subscriptions page.
  popular: { kind: 'feed' },
  subscriptions: gatedScreen('subscriptions'),
  playlists: gatedScreen('playlists'),
  history: gatedScreen('history'),
  channels: gatedScreen('channels'),
};

export function screenForRoute(route: Route): Screen {
  switch (route.kind) {
    case 'home':
      return { kind: 'home' };

    case 'watch':
      return {
        kind: 'watch',
        videoId: route.videoId,
        startSec: route.startSec,
        playlistId: route.playlistId,
        index: route.index,
      };

    case 'embed':
      return {
        kind: 'embed',
        videoId: route.videoId,
        startSec: route.startSec,
        options: route.options,
        list: route.list,
      };

    case 'channel':
      return {
        kind: 'channel',
        channelId: route.channelId,
        tab: route.tab,
        sort: route.sort,
        search: null,
      };

    case 'channelSearch':
      // The channel page, wearing the search: the tab and sort are the defaults the header links
      // format back from, and the body is decided by `search` alone.
      return {
        kind: 'channel',
        channelId: route.channelId,
        tab: DEFAULT_CHANNEL_TAB,
        sort: DEFAULT_CHANNEL_SORT,
        search: { q: route.q, page: route.page },
      };

    case 'channelName':
    case 'clip':
      // `href` is only React's key. It has to be in the screen rather than derived in `App.tsx`,
      // because building it means `formatRoute` and a `.tsx` may not import core (R13).
      return { kind: 'resolve', route, href: formatRoute(route) };

    case 'post':
      return {
        kind: 'placeholder',
        title: text().shell.post.title,
        detail: text().shell.post.detail,
        // Deliberately `null`, and this is the one placeholder where that does not mean "a typo". §8
        // asks for an explicit page rather than a 404, and promising a slice for something the plan
        // defers past v1 would be a different lie from the one a 404 tells.
        slice: null,
        homeHref: HOME_HREF,
        jumpOuts: jumpOutLinks(null, route),
      };

    case 'hashtag':
      return { kind: 'hashtag', tag: route.tag, page: route.page };

    case 'playlist':
      return { kind: 'playlist', playlistId: route.playlistId };

    case 'search':
      return { kind: 'search', search: route.search };

    case 'feed':
      return FEED_SCREENS[route.feed];

    case 'login':
      return { kind: 'login', next: route.next };

    case 'settings':
      return { kind: 'settings', tab: route.tab };

    case 'not-found':
      return {
        kind: 'placeholder',
        title: text().shell.notFound.title,
        detail: text().shell.notFound.detail(route.path),
        // Deliberately `null`: this arm is also where a typo lands, and promising a slice for
        // `/garbage` would be a lie told to everyone who mistypes a URL.
        slice: null,
        homeHref: HOME_HREF,
        jumpOuts: [],
      };
  }
}

/** The screen for the address bar, re-derived whenever it changes. */
export function useScreen(): Screen {
  return screenForRoute(useRoute());
}
